import React, { useState } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Upload, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "./utils";

export function OtherDocumentsContent({ projectId, documents, onRefresh, canWrite, token }) {
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ document_name: "", file: null });

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.file || !formData.document_name) {
      toast.error("Please provide document name and select a file");
      return;
    }
    
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", formData.file);
      data.append("document_name", formData.document_name);
      
      await axios.post(`${API}/projects/${projectId}/documents`, data, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Document uploaded successfully!");
      setShowForm(false);
      setFormData({ document_name: "", file: null });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename || doc.document_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Document downloaded!");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  
  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="flex justify-end">
          <Button 
            onClick={() => setShowForm(!showForm)} 
            className="bg-brillien-600 hover:bg-brillien-700"
            data-testid="upload-document-btn"
          >
            <Upload className="h-4 w-4 mr-2" />Upload Document
          </Button>
        </div>
      )}

      {showForm && (
        <Card className="p-4 border-brillien-200 bg-brillien-50">
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Document Name *</Label>
                <Input 
                  value={formData.document_name}
                  onChange={(e) => setFormData({...formData, document_name: e.target.value})}
                  placeholder="Enter document name"
                  required
                  data-testid="document-name-input"
                />
              </div>
              <div>
                <Label>Select File *</Label>
                <Input 
                  type="file"
                  onChange={(e) => setFormData({...formData, file: e.target.files[0]})}
                  required
                  data-testid="document-file-input"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={uploading} className="bg-brillien-600 hover:bg-brillien-700">
                {uploading ? "Uploading..." : "Upload"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {documents.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No documents uploaded yet</p>
          {canWrite && <p className="text-sm mt-1">Click "Upload Document" to add one</p>}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">S.No</TableHead>
              <TableHead className="font-semibold">Document Name</TableHead>
              <TableHead className="font-semibold">Uploaded Date</TableHead>
              <TableHead className="font-semibold">Uploaded By</TableHead>
              <TableHead className="text-right font-semibold">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc, index) => (
              <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                <TableCell className="font-mono text-sm text-slate-600">{index + 1}</TableCell>
                <TableCell className="font-medium text-slate-900">{doc.document_name || doc.name}</TableCell>
                <TableCell className="text-sm text-slate-600">{formatDate(doc.uploaded_at || doc.created_at)}</TableCell>
                <TableCell className="text-sm text-slate-600">{doc.uploaded_by || "-"}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDownload(doc)}
                      className="text-brillien-600 hover:text-brillien-700"
                      data-testid={`download-doc-${doc.id}`}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    {canWrite && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDelete(doc.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
